import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { startOfDay, startOfWeek, startOfMonth, endOfDay } from 'date-fns';

export interface SDRConnectionMetric {
  userId: string;
  userName: string;
  callTransfers: number;
  videoConnections: number;
  totalConnections: number;
}

export interface ConnectionMetrics {
  totalCallTransfers: number;
  totalVideoConnections: number;
  totalConnections: number;
  bySDR: SDRConnectionMetric[];
  loading: boolean;
}

export interface PeriodConnectionMetrics {
  today: ConnectionMetrics;
  week: ConnectionMetrics;
  month: ConnectionMetrics;
  custom: ConnectionMetrics;
  loading: boolean;
  lastUpdate: Date | null;
  refresh: () => void;
}

const emptyMetrics: ConnectionMetrics = {
  totalCallTransfers: 0,
  totalVideoConnections: 0,
  totalConnections: 0,
  bySDR: [],
  loading: true,
};

export function useSDRConnectionMetrics(dateRange?: { from: Date | null; to: Date | null }): PeriodConnectionMetrics {
  const [todayMetrics, setTodayMetrics] = useState<ConnectionMetrics>(emptyMetrics);
  const [weekMetrics, setWeekMetrics] = useState<ConnectionMetrics>(emptyMetrics);
  const [monthMetrics, setMonthMetrics] = useState<ConnectionMetrics>(emptyMetrics);
  const [customMetrics, setCustomMetrics] = useState<ConnectionMetrics>(emptyMetrics);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const loadMetrics = useCallback(async () => {
    try {
      setLoading(true);

      const now = new Date();
      const todayStart = startOfDay(now);
      const todayEnd = endOfDay(now);
      const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday
      const monthStart = startOfMonth(now);

      // Detect "All Time" mode: dateRange exists but from/to are null
      const isAllTime = dateRange !== undefined && dateRange.from === null && dateRange.to === null;
      const allTimeStart = new Date('2020-01-01');
      
      console.log('[SDR Metrics] dateRange:', dateRange);
      console.log('[SDR Metrics] isAllTime:', isAllTime);
      console.log('[SDR Metrics] queryStartDate will be:', isAllTime ? allTimeStart : (dateRange?.from || monthStart));

      // Use appropriate start date based on filter type
      const queryStartDate = isAllTime 
        ? allTimeStart 
        : (dateRange?.from || monthStart);
      
      const queryEndDate = isAllTime 
        ? todayEnd 
        : (dateRange?.to ? endOfDay(dateRange.to) : todayEnd);

      // Fetch SDR users (from dashboard_visibility or by role)
      const { data: sdrVisibility } = await supabase
        .from('dashboard_visibility')
        .select('user_id')
        .eq('dashboard_type', 'sdr')
        .eq('is_visible', true);

      const sdrUserIds = sdrVisibility?.map(v => v.user_id) || [];

      // Fetch user details (including email for fallback matching)
      const { data: users } = await supabase
        .from('app_users')
        .select('id, display_name, first_name, last_name, email')
        .eq('active', true);

      const userMap = new Map(
        (users || []).map(u => [
          u.id,
          u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Unknown'
        ])
      );

      // Create email -> SDR ID mapping for fallback on legacy data
      const emailToSdrId = new Map<string, string>();
      (users || []).forEach(u => {
        if (u.email && sdrUserIds.includes(u.id)) {
          emailToSdrId.set(u.email.toLowerCase(), u.id);
        }
      });

      // Fetch call transfers - use queryStartDate to get historical data
      // NOTE: Only require transferred_by (not transferred_to) to count "Ring All" transfers
      const { data: callTransfers } = await supabase
        .from('calls')
        .select('id, transferred_by, transferred_at, transferred_to')
        .not('transferred_by', 'is', null)
        .gte('transferred_at', queryStartDate.toISOString());

      // Fetch video call connections from BOTH sources in parallel
      const [ringResult, scheduledResult] = await Promise.all([
        // 1. Video Now (ring requests)
        supabase
          .from('video_call_ring_requests' as any)
          .select('id, requested_by, accepted_at, connection_confirmed')
          .eq('status', 'accepted')
          .gte('accepted_at', queryStartDate.toISOString()),

        // 2. Scheduled video calls with confirmed connections (include attendees for fallback)
        supabase
          .from('calls')
          .select('id, created_by, scheduled_by, attendees, completed_at, connection_confirmed')
          .eq('channel', 'video')
          .eq('status', 'completed')
          .gte('completed_at', queryStartDate.toISOString())
      ]);

      const videoRingConnections = ringResult.data || [];
      const scheduledVideoConnections = scheduledResult.data || [];

      // REMOVED: Presumed connection logic - now only count explicitly confirmed connections

      // Helper function to find SDR from attendees (for legacy data without scheduled_by)
      const findSdrFromAttendees = (attendees: any): string | null => {
        if (!attendees || !Array.isArray(attendees)) return null;
        for (const attendee of attendees) {
          const email = typeof attendee === 'string' ? attendee : attendee?.email;
          if (email && emailToSdrId.has(email.toLowerCase())) {
            return emailToSdrId.get(email.toLowerCase()) || null;
          }
        }
        return null;
      };

      // Process metrics for each period
      const processMetrics = (
        transfers: typeof callTransfers,
        ringVideos: typeof videoRingConnections,
        scheduledVideos: typeof scheduledVideoConnections,
        startDate: Date,
        endDate: Date
      ): ConnectionMetrics => {
        // Filter transfers - ONLY count SDRs
        const filteredTransfers = (transfers || []).filter(t => {
          const sdrId = t.transferred_by;
          if (!sdrUserIds.includes(sdrId)) return false; // Only count SDRs
          
          const date = new Date(t.transferred_at);
          return date >= startDate && date <= endDate;
        });

        // Video Now (ring) calls - ONLY count EXPLICITLY CONFIRMED connections by SDRs
        const filteredRingVideos = (ringVideos || []).filter((v: any) => {
          const sdrId = v.requested_by;
          if (!sdrUserIds.includes(sdrId)) return false; // Only count SDRs
          
          const date = new Date(v.accepted_at);
          if (date < startDate || date > endDate) return false;

          // ONLY count explicitly confirmed connections
          return v.connection_confirmed === true;
        });

        // Scheduled video calls - ONLY count EXPLICITLY CONFIRMED connections by SDRs
        const filteredScheduledVideos = (scheduledVideos || []).filter((v: any) => {
          // Determine SDR: scheduled_by, fallback to attendees, then created_by
          let sdrId = v.scheduled_by;
          if (!sdrId) {
            sdrId = findSdrFromAttendees(v.attendees);
          }
          if (!sdrId) {
            sdrId = v.created_by;
          }
          
          if (!sdrId || !sdrUserIds.includes(sdrId)) return false; // Only count SDRs
          
          const date = new Date(v.completed_at);
          if (date < startDate || date > endDate) return false;

          // ONLY count explicitly confirmed connections
          return v.connection_confirmed === true;
        });

        // Combine both video sources
        const allVideoConnections = [...filteredRingVideos, ...filteredScheduledVideos];

        // Count by SDR - all items are already filtered to SDRs only
        const sdrStats = new Map<string, { transfers: number; videos: number }>();

        filteredTransfers.forEach(t => {
          const sdrId = t.transferred_by;
          const current = sdrStats.get(sdrId) || { transfers: 0, videos: 0 };
          current.transfers++;
          sdrStats.set(sdrId, current);
        });

        // Count ring videos (Video Now) - use requested_by
        filteredRingVideos.forEach((v: any) => {
          const sdrId = v.requested_by;
          const current = sdrStats.get(sdrId) || { transfers: 0, videos: 0 };
          current.videos++;
          sdrStats.set(sdrId, current);
        });

        // Count scheduled videos - use scheduled_by, fallback to attendees, then created_by
        filteredScheduledVideos.forEach((v: any) => {
          let sdrId = v.scheduled_by;
          if (!sdrId) {
            sdrId = findSdrFromAttendees(v.attendees);
          }
          if (!sdrId) {
            sdrId = v.created_by;
          }
          const current = sdrStats.get(sdrId) || { transfers: 0, videos: 0 };
          current.videos++;
          sdrStats.set(sdrId, current);
        });

        const bySDR: SDRConnectionMetric[] = Array.from(sdrStats.entries())
          .map(([userId, stats]) => ({
            userId,
            userName: userMap.get(userId) || 'Unknown',
            callTransfers: stats.transfers,
            videoConnections: stats.videos,
            totalConnections: stats.transfers + stats.videos,
          }))
          .sort((a, b) => b.totalConnections - a.totalConnections);

        return {
          totalCallTransfers: filteredTransfers.length,
          totalVideoConnections: allVideoConnections.length,
          totalConnections: filteredTransfers.length + allVideoConnections.length,
          bySDR,
          loading: false,
        };
      };

      // Calculate metrics for each period
      setTodayMetrics(processMetrics(callTransfers, videoRingConnections, scheduledVideoConnections, todayStart, todayEnd));
      setWeekMetrics(processMetrics(callTransfers, videoRingConnections, scheduledVideoConnections, weekStart, todayEnd));
      setMonthMetrics(processMetrics(callTransfers, videoRingConnections, scheduledVideoConnections, monthStart, todayEnd));
      
      // Calculate custom metrics based on filter type
      if (isAllTime) {
        // All time - use early date as start
        setCustomMetrics(processMetrics(callTransfers, videoRingConnections, scheduledVideoConnections, allTimeStart, todayEnd));
      } else if (dateRange?.from) {
        // Custom period with valid dates
        setCustomMetrics(processMetrics(callTransfers, videoRingConnections, scheduledVideoConnections, dateRange.from, queryEndDate));
      } else {
        // Fallback to current month
        setCustomMetrics(processMetrics(callTransfers, videoRingConnections, scheduledVideoConnections, monthStart, todayEnd));
      }

      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading SDR connection metrics:', error);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(dateRange)]);

  useEffect(() => {
    loadMetrics();

    // Auto-refresh every 5 minutes (reduced from 60s to avoid excessive database load)
    const interval = setInterval(loadMetrics, 300000);
    return () => clearInterval(interval);
  }, [loadMetrics]);

  return {
    today: todayMetrics,
    week: weekMetrics,
    month: monthMetrics,
    custom: customMetrics,
    loading,
    lastUpdate,
    refresh: loadMetrics,
  };
}
